/**
 * ContentHost — the seam that lets one slot machine drive five kinds of card.
 *
 * The carousel windows slots N-1/N/N+1 and drives a lifecycle: load, resume, pause, exit,
 * destroy. None of that is specific to WebAssembly. What IS specific lives in GameHost.load(),
 * which unconditionally constructs a Worker and transfers two OffscreenCanvases — so a clip
 * (a <video>), a thread (plain DOM) or a web card (a sandboxed iframe) cannot occupy a slot
 * today no matter what the feed says.
 *
 * This interface is the contract each class implements. The carousel talks to it and never
 * asks what is inside.
 *
 * ON THE FACTORY THROWING
 * createContentHost() refuses the classes that have no implementation instead of returning
 * something inert. A silent no-op host is the failure mode where "one primitive, five classes"
 * stops being true without anyone noticing: the feed renders a blank slot, telemetry records a
 * session, and the class looks supported. Failing loudly keeps the gap visible, and the test
 * suite asserts the refusal is explicit for every unimplemented class.
 */

import type { Card, SoloCard } from './content';
import type { GameMeta, HostState, ReadyMessage } from './protocol';
import type { ExitCondition, LiveStats, MechanicalTelemetry } from '../telemetry/TelemetryTypes';

/**
 * The lifecycle every card class must satisfy. Deliberately the lifecycle GameHost already
 * exposes, so the wasm path is the reference implementation rather than a special case.
 */
export interface ContentHost {
  readonly serial: number;
  state: HostState;
  sessionId: string;

  /** Fetch and prepare, ending paused and ready to present. */
  load(): Promise<ReadyMessage>;
  /** Begin a micro-session. */
  resume(): void;
  pause(reason: 'swipe' | 'idle' | 'hidden' | 'manual'): void;
  /** End the micro-session with a reason; emits the final telemetry batch. */
  exit(condition: ExitCondition): void;
  /** Release everything. Must leave no worker, canvas, element or listener behind. */
  destroy(): void;
}

/** Events a host emits upward. A class that cannot produce one simply never emits it. */
export interface ContentHostEvents {
  state: (state: HostState, host: ContentHost) => void;
  ready: (info: ReadyMessage, host: ContentHost) => void;
  stats: (stats: LiveStats, host: ContentHost) => void;
  telemetry: (batch: MechanicalTelemetry, host: ContentHost) => void;
  error: (message: string, phase: string, host: ContentHost) => void;
}

/**
 * Bridge a solo card onto the Phase 1 record GameHost still speaks.
 *
 * Kept deliberately small and one-directional: the union is the real model, and this exists
 * only so the wasm host can be migrated without rewriting it in the same change.
 */
export function soloCardToGameMeta(card: SoloCard): GameMeta {
  return {
    id: card.id,
    title: card.title,
    developer: card.creator.handle,
    wasm_url: card.mechanic.url,
    data_url: card.contentpack?.url ?? null,
    thumbnail_url: card.thumbnail_url,
    verbs: card.verbs,
    accent: card.accent,
    description: card.description,
    local: card.local,
  };
}

/**
 * Serial numbers identify a host INSTANCE across its whole lifetime, and the leak test relies
 * on them being unique across the feed. Every host class must draw from this one counter --
 * a per-class counter silently hands two different hosts the same serial.
 */
let hostSerial = 0;
export function nextHostSerial(): number {
  return ++hostSerial;
}

export class UnsupportedCardClass extends Error {
  constructor(
    readonly cardClass: Card['class'],
    reason: string,
  ) {
    super(`no ContentHost for card class "${cardClass}": ${reason}`);
    this.name = 'UnsupportedCardClass';
  }
}

/**
 * Why each class is not yet hosted. Written down rather than implied, because these are the
 * concrete gaps between the architecture and what actually runs.
 */
export const UNIMPLEMENTED: Record<Exclude<Card['class'], 'solo'>, string> = {
  live: 'no matchmaking, presence or lockstep transport exists yet; the simulation is deterministic (see SimClock) but there is no backend to synchronise it with',
  clip: 'needs a video element host and clip generation from a recorded run',
  thread: 'needs a DOM host plus the moderation surface that user-authored text requires',
  web: 'hosted by WebCardHost on the GameMeta path in the carousel; not yet wired through createContentHost',
};

/**
 * Build the host for a card, or refuse with a reason.
 *
 * `makeWasmHost` is injected so this module stays free of the worker/DOM machinery and can be
 * exercised in a plain node test.
 */
export function createContentHost(card: Card, makeWasmHost: (meta: GameMeta) => ContentHost): ContentHost {
  switch (card.class) {
    case 'solo':
      return makeWasmHost(soloCardToGameMeta(card));
    case 'live':
    case 'clip':
    case 'thread':
    case 'web':
      throw new UnsupportedCardClass(card.class, UNIMPLEMENTED[card.class]);
  }
}

/** Which classes the runtime can actually present today. Used by the feed to filter. */
export function isHostable(card: Card): boolean {
  return card.class === 'solo';
}
